/* 
This hook does the following:
1. Pulls down all upcoming events plus the events that I have liked/interested
2. Returns the filtered object to the EventCalendar component for use in the feed
 */

import { EventsResponse, FullEvent, FullPost } from 'model';
import { useMemo } from 'react';
import { useGetFavoritePostsQuery, useGetUpcomingEventsQuery } from '../api/PostApi';
import { useCurrentUser } from './useCurrentUser';
import { useLoggedIn } from './useLoggedIn';

interface EventFilterType {
	filteredEvents: EventsResponse | undefined;
	loadingEvents: boolean;
}

/**
 * Filter the favorite events based on the user filter value
 * @param {any[]} myInterestedEvents A list of events that I either created or liked
 * @param {string} userId My user id
 * @param {boolean} mine Whether or not I want my events
 * @returns {any[]} A list of events that I created if 'mine===true' or liked if 'mine===false'
 */
function filterFavorites(myInterestedEvents: { posts: FullPost[]; ratings: number[] }, userId: string, mine: boolean) {
	const filteredArrays = myInterestedEvents.posts.reduce(
		(acc: [FullPost[], number[]], value, index) => {
			if ((mine && value.author === userId) || (!mine && value.author !== userId)) {
				acc[0].push(value);
				acc[1].push(myInterestedEvents.ratings[index]);
			}
			return acc;
		},
		[[], []] as [FullPost[], number[]]
	);

	return {
		events: filteredArrays[0] as FullEvent[],
		interest: filteredArrays[1]
	};
}

export const useEventFilters = (userFilterValue: boolean, myInterestedEventsFilterValue: boolean): EventFilterType => {
	const loggedIn = useLoggedIn();
	const { data: upcomingEvents, isFetching: upcomingLoading } = useGetUpcomingEventsQuery({
		skip: !loggedIn.loggedIn
	});
	const { data: myInterestedEvents, isFetching: interestedLoading } = useGetFavoritePostsQuery(
		{
			postType: 'event',
			dayBack: 90
		},
		{ skip: !loggedIn.realUser }
	);
	const user = useCurrentUser();
	const filteredEvents = useMemo(() => {
		if (myInterestedEventsFilterValue || userFilterValue) {
			if (myInterestedEvents) {
				if (!myInterestedEventsFilterValue) {
					return filterFavorites(myInterestedEvents, user._id, true);
				}
				if (!userFilterValue) {
					return filterFavorites(myInterestedEvents, user._id, false);
				}
				return {
					events: myInterestedEvents.posts as FullEvent[],
					interest: myInterestedEvents.ratings
				};
			} else {
				return { events: [], interest: [] };
			}
		}
		if (upcomingEvents) {
			return {
				events: upcomingEvents.events as FullEvent[],
				interest: upcomingEvents.interest
			};
		} else {
			return { events: [], interest: [] };
		}
	}, [myInterestedEvents, myInterestedEventsFilterValue, upcomingEvents, userFilterValue, user._id]);

	return { filteredEvents, loadingEvents: upcomingLoading || interestedLoading };
};
